"use client";

import { useCallback, useReducer, useRef, type SyntheticEvent } from "react";

import { gateReducer, parseWorkEmail, SAVE_FAILED_MESSAGE, type Gate } from "../_logic/public/gate";

/** Capture-phase handlers for the element that holds the sample estimator. */
export interface GateBoundaryProps {
  onPointerDownCapture: (event: React.PointerEvent) => void;
  onMouseDownCapture: (event: React.MouseEvent) => void;
  onClickCapture: (event: React.MouseEvent) => void;
  onKeyDownCapture: (event: React.KeyboardEvent) => void;
  onFocusCapture: (event: React.FocusEvent) => void;
  onPasteCapture: (event: React.ClipboardEvent) => void;
  onDropCapture: (event: React.DragEvent) => void;
}

/** The public estimator's email gate. */
export interface EstimateGate {
  gate: Gate;
  /** Spread on every element that wraps the sample; the email ask must sit outside them. */
  boundary: GateBoundaryProps;
  /** The email field, focused whenever focus reaches the sample after it locked. */
  emailInput: React.RefObject<HTMLInputElement>;
  /** Checks the email, then hands it to `continueWith`; a failure shows as an error. */
  submit: (raw: string) => Promise<void>;
}

const block = (event: SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * Keeps the sample estimator a preview: nothing inside it reacts, and the first click, key or
 * focus locks the gate so the page can ask for an email. Handlers run in the capture phase,
 * so the estimator never sees the event and the sample never changes. A touch only locks on its
 * click, so a swipe that starts on the estimator still scrolls the page.
 * @param continueWith - Saves the email and leaves for sign-up. It rejects when the save failed.
 * @returns The gate, the boundary handlers, the email field ref and `submit`.
 * @throws Never.
 */
export function useEstimateGate(continueWith: (email: string) => Promise<void>): EstimateGate {
  // States
  const [gate, dispatch] = useReducer(gateReducer, { status: "preview" });
  const emailInput = useRef<HTMLInputElement>(null);
  const sending = useRef(false);

  // Handlers
  const lock = useCallback(() => {
    dispatch({ type: "interact" });
    emailInput.current?.focus();
  }, []);

  const submit = useCallback(
    async (raw: string) => {
      if (sending.current) return;
      const parsed = parseWorkEmail(raw);
      if (!parsed.ok) {
        dispatch({ type: "fail", email: raw, message: parsed.message });
        return;
      }
      sending.current = true;
      dispatch({ type: "submit", email: parsed.email });
      try {
        await continueWith(parsed.email);
      } catch {
        dispatch({ type: "fail", email: parsed.email, message: SAVE_FAILED_MESSAGE });
      } finally {
        sending.current = false;
      }
    },
    [continueWith]
  );

  const boundary: GateBoundaryProps = {
    onPointerDownCapture: (event) => {
      event.stopPropagation();
      // A mouse press would focus and select; a touch is left alone so the page still scrolls.
      if (event.pointerType === "mouse") event.preventDefault();
    },
    onMouseDownCapture: block,
    onClickCapture: (event) => {
      block(event);
      lock();
    },
    onKeyDownCapture: (event) => {
      // Tab moves focus on through the page; focus landing in the sample locks it below.
      if (event.key === "Tab") return;
      block(event);
      lock();
    },
    onFocusCapture: (event) => {
      event.stopPropagation();
      lock();
    },
    onPasteCapture: block,
    onDropCapture: block,
  };

  return { gate, boundary, emailInput, submit };
}
